import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import {
  leadsApi,
  type LeadGeneratorOption,
  type LeadSource,
  type UpsertBrokerLeadPayload,
} from "@/api/leads";
import { leadKeys } from "@/hooks/leads/leadKeys";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { LeadGeneratorPicker } from "@/components/broker-crm/LeadGeneratorPicker";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { US_STATES } from "@/lib/usStates";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";

const SOURCES: LeadSource[] = [
  "ColdCall", "InboundInquiry", "TradeShow", "Referral", "WebForm",
  "SocialMedia", "IBBA", "MaSource", "BizBuySell", "LinkedIn",
  "PurchasedList", "Other",
];

const EMPTY_FORM: UpsertBrokerLeadPayload = {
  firstName: "",
  lastName: "",
  companyName: "",
  title: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  state: "",
  source: "ColdCall",
  sourceDetail: "",
  interestLevel: "Unknown",
  notes: "",
  nextFollowUpDate: null,
  zip: "",
  sourceGeneratorId: null,
};

/**
 * Full New Lead / Edit Lead page. Handles both /leads/new and
 * /leads/:id/edit, mirroring the BrokerEditPage convention — a dedicated
 * page rather than a modal so the form can grow. The lighter-weight
 * QuickAddLeadModal remains the high-volume entry path from the list.
 */
export function LeadEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editing = !!id;

  const [form, setForm] = useState<UpsertBrokerLeadPayload>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  // Lead Generation — the selected generator's display name (the create payload
  // only carries the id; keep the label for the picker readout).
  const [generator, setGenerator] = useState<LeadGeneratorOption | null>(null);

  const existing = useQuery({
    queryKey: leadKeys.detail(id ?? ""),
    queryFn: () => leadsApi.get(id!),
    enabled: editing,
  });

  // Default breadcrumb is URL-derived, which renders the raw lead GUID. Show
  // the lead's name (linking back to the detail page) instead.
  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Leads", href: "/leads" },
            {
              label: `${existing.data.firstName} ${existing.data.lastName}`.trim() || "Lead",
              href: `/leads/${id}`,
            },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Leads", href: "/leads" },
          { label: "New lead" },
        ],
  );

  useEffect(() => {
    if (existing.data) {
      const l = existing.data;
      setForm({
        firstName: l.firstName,
        lastName: l.lastName,
        companyName: l.companyName ?? "",
        title: l.title ?? "",
        phone: l.phone ?? "",
        email: l.email ?? "",
        address: l.address ?? "",
        city: l.city ?? "",
        state: l.state ?? "",
        source: l.source,
        sourceDetail: l.sourceDetail ?? "",
        interestLevel: l.interestLevel,
        notes: l.notes ?? "",
        nextFollowUpDate: l.nextFollowUpDate ?? null,
        zip: l.zip ?? "",
        sourceGeneratorId: l.sourceGeneratorId ?? null,
      });
    }
  }, [existing.data]);

  const set = <K extends keyof UpsertBrokerLeadPayload>(key: K, val: UpsertBrokerLeadPayload[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  // Both email and phone are optional, but if the user types anything the
  // value must be a valid format. Empty/whitespace clears the field so
  // existing bad data can be wiped without fixing the format first.
  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });

  const mutation = useMutation({
    mutationFn: (body: UpsertBrokerLeadPayload) =>
      editing ? leadsApi.update(id!, body) : leadsApi.create(body),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
      navigate(`/leads/${res.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const save = () => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    if (emailError || phoneError) {
      setError(emailError ?? phoneError ?? null);
      return;
    }
    mutation.mutate({
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      companyName: form.companyName?.trim() || null,
      title: form.title?.trim() || null,
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      address: form.address?.trim() || null,
      city: form.city?.trim() || null,
      state: form.state || null,
      zip: form.zip?.trim() || null,
      sourceDetail: form.sourceDetail?.trim() || null,
      notes: form.notes?.trim() || null,
    });
  };

  return (
    <>
      <PageHeader
        eyebrow="Leads"
        title={editing ? "Edit lead" : "New lead"}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              size="sm"
              loading={mutation.isPending}
              disabled={!form.firstName.trim() || !form.lastName.trim() || !!emailError || !!phoneError}
              onClick={save}
            >
              <Save size={14} strokeWidth={1.5} aria-hidden /> Save lead
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="First name" required><Input value={form.firstName} autoFocus onChange={(e) => set("firstName", e.target.value)} /></FormField>
        <FormField label="Last name" required><Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} /></FormField>
        <FormField label="Email" error={emailError}>
          <Input type="email" value={form.email ?? ""} invalid={!!emailError} onChange={(e) => set("email", e.target.value)} />
        </FormField>
        <FormField label="Phone" error={phoneError}>
          <Input type="tel" value={form.phone ?? ""} invalid={!!phoneError} onChange={(e) => set("phone", e.target.value)} />
        </FormField>
        <FormField label="Company Name" helperText="If you're an independent contractor or employed by a brokerage and do not have your own company, leave this blank.">
          <Input value={form.companyName ?? ""} onChange={(e) => set("companyName", e.target.value)} />
        </FormField>
        <FormField label="Title"><Input value={form.title ?? ""} onChange={(e) => set("title", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Address" className="md:col-span-2"><Input value={form.address ?? ""} onChange={(e) => set("address", e.target.value)} /></FormField>
        <FormField label="City"><Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} /></FormField>
        <FormField label="State">
          <Select value={form.state ?? ""} onChange={(e) => set("state", e.target.value)}>
            <option value="">—</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
          </Select>
        </FormField>
        <FormField label="ZIP"><Input value={form.zip ?? ""} onChange={(e) => set("zip", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Source</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Source">
          <Select value={form.source} onChange={(e) => set("source", e.target.value as LeadSource)}>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormField>
        <FormField label="Source detail"><Input value={form.sourceDetail ?? ""} onChange={(e) => set("sourceDetail", e.target.value)} /></FormField>
      </div>

      {/* Lead Generation — Ownership attribution. On the New Lead form, attribute
          an inbound caller to the Lead Generator who reached out to them; on save
          the generator's matching prelead is linked (or created) and marked
          Escalated. Existing leads change/clear attribution from the detail page's
          Ownership section. */}
      {!editing && (
        <>
          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Ownership</h3>
          <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
            <FormField
              label="Lead Generator"
              helperText="If this caller was reached out to by a lead generator, attribute the lead to them so they earn credit when you mark it Contacted."
            >
              <LeadGeneratorPicker
                value={form.sourceGeneratorId ?? null}
                valueLabel={generator?.displayName ?? null}
                onChange={(opt) => {
                  set("sourceGeneratorId", opt?.id ?? null);
                  setGenerator(opt);
                }}
              />
            </FormField>
          </div>
        </>
      )}

      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}
    </>
  );
}
